import { useState } from 'react';
import JamHeader from './JamHeader';

export default function JamSessionMenuPreview() {
  const [setlistName, setSetlistName] = useState('Jazz Standards');
  const [songTitle, setSongTitle] = useState('Autumn Leaves');
  const [transpose, setTranspose] = useState(0);
  const [isHost, setIsHost] = useState(false);

  const handleTranspose = (amount: number) => {
    setTranspose(prev => Math.max(-12, Math.min(12, prev + amount)));
  };

  const presetScenarios = [
    { setlist: 'Jazz Standards', song: 'Autumn Leaves', transpose: 2 },
    { setlist: 'Blues Set', song: 'Sweet Home Chicago', transpose: -1 },
    { setlist: 'Latin Night', song: 'Girl from Ipanema', transpose: 0 },
    { setlist: '', song: 'Untitled Chart', transpose: 0 },
    { setlist: 'Very Long Setlist Name That Should Truncate', song: 'Very Long Song Title That Should Also Truncate Properly', transpose: 3 },
  ];

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-white text-3xl font-bold mb-2">🎵 Jam Session Header Preview</h1>
        <p className="text-gray-400 mb-8">Interactive preview of the new Jam Session header (Host & Client modes)</p>

        {/* Controls Panel */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8 space-y-6">
          <h2 className="text-white text-xl font-semibold mb-4">Controls</h2>

          {/* Role Toggle */}
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Session Role</label>
            <div className="flex gap-2">
              <button
                onClick={() => setIsHost(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isHost
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                👑 HOST
              </button>
              <button
                onClick={() => setIsHost(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  !isHost
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                🎸 CLIENT
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {isHost 
                ? 'HOST: Has Open Chart, Open Setlist buttons + Edition Mode button'
                : 'CLIENT: No Open Chart/Setlist buttons, only Leave Session button'
              }
            </p>
          </div>

          {/* Setlist Name Input */}
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Setlist Name</label>
            <input
              type="text"
              value={setlistName}
              onChange={(e) => setSetlistName(e.target.value)}
              placeholder="Leave empty to hide setlist"
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
            />
          </div>

          {/* Song Title Input */}
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Current Song Title</label>
            <input
              type="text"
              value={songTitle}
              onChange={(e) => setSongTitle(e.target.value)}
              placeholder="Enter song title"
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
            />
          </div>

          {/* Preset Scenarios */}
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Quick Presets</label>
            <div className="flex flex-wrap gap-2">
              {presetScenarios.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSetlistName(preset.setlist);
                    setSongTitle(preset.song);
                    setTranspose(preset.transpose);
                  }}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-all"
                >
                  {preset.setlist || 'No Setlist'} - {preset.song.substring(0, 15)}...
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="space-y-8">
          <div>
            <h2 className="text-white text-xl font-semibold mb-4">
              {isHost ? '👑 HOST View' : '🎸 CLIENT View'}
            </h2>
            <p className="text-gray-400 text-sm mb-4">
              {isHost 
                ? 'Full controls: Open Chart, Open Setlist, navigation arrows, Edition Mode button'
                : 'Read-only view: No Open Chart/Setlist, disabled navigation, Leave Session button'
              }
            </p>
            
            {/* Preview Container */}
            <div className="bg-black rounded-lg overflow-hidden">
              <JamHeader
                collectionName={setlistName}
                currentSongTitle={songTitle}
                canNavigatePrev={isHost}
                canNavigateNext={isHost}
                onNavigatePrev={() => console.log('Navigate prev')}
                onNavigateNext={() => console.log('Navigate next')}
                onOpenChart={() => console.log('Open Chart clicked')}
                onOpenSetlist={() => console.log('Open Setlist clicked')}
                isHost={isHost}
                showEditionModeButton={true}
                onEditionMode={() => console.log(isHost ? 'Edition Mode clicked' : 'Leave Session clicked')}
              />

              {/* Transpose Controls (same as in App.tsx) */}
              <div className="flex justify-start items-start mt-1 px-4 py-2 bg-black">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTranspose(-1)}
                    className="flex items-center justify-center px-3 py-2 rounded-lg text-sm font-bold text-neutral-300 hover:text-white hover:bg-neutral-900 transition-colors border border-neutral-800 hover:border-neutral-700 bg-neutral-900/50"
                    title="Transpose down"
                  >
                    −
                  </button>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-neutral-300 border border-neutral-800 bg-neutral-900/50 min-w-[5rem] justify-center">
                    <span className="text-neutral-400 text-xs uppercase tracking-wider">Transpose</span>
                    <span className="text-white font-bold">
                      {transpose >= 0 ? '+' : ''}{transpose}
                    </span>
                  </div>
                  <button
                    onClick={() => handleTranspose(1)}
                    className="flex items-center justify-center px-3 py-2 rounded-lg text-sm font-bold text-neutral-300 hover:text-white hover:bg-neutral-900 transition-colors border border-neutral-800 hover:border-neutral-700 bg-neutral-900/50"
                    title="Transpose up"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Placeholder content */}
              <div className="p-8 text-center text-gray-500 text-sm bg-gray-950">
                <p>↑ Jam Session Header Component ↑</p>
                <p className="mt-2">Chart content would appear here...</p>
              </div>
            </div>
          </div>

          {/* Comparison with GigHeader */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white text-lg font-semibold mb-3">📋 Differences from GIG MODE</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-purple-400 font-medium mb-2">JAM SESSION Header</h4>
                <ul className="text-gray-300 space-y-1 text-sm">
                  <li>• Label: "JAM SESSION" (purple badge)</li>
                  <li>• Border: Purple (border-purple-500)</li>
                  <li>• Icons: Purple stroke color</li>
                  <li>• Faster ping animation (1.5s)</li>
                  <li>• HOST: Full controls</li>
                  <li>• CLIENT: No Open Chart/Setlist</li>
                  <li>• CLIENT: "Leave Session" button (red)</li>
                </ul>
              </div>
              <div>
                <h4 className="text-yellow-400 font-medium mb-2">GIG MODE Header</h4>
                <ul className="text-gray-300 space-y-1 text-sm">
                  <li>• Label: "GIG MODE" (purple badge)</li>
                  <li>• Border: Gold (border-accent-500)</li>
                  <li>• Icons: Gold stroke color</li>
                  <li>• Standard ping animation (2s)</li>
                  <li>• Always has Open Chart/Setlist</li>
                  <li>• "Edition Mode" button (green)</li>
                  <li>• No role distinction</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Implementation Notes */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white text-lg font-semibold mb-3">🔧 Implementation Details</h3>
            <div className="text-gray-300 space-y-2 text-sm">
              <p><strong>Component:</strong> <code className="bg-gray-700 px-2 py-1 rounded">components/JamHeader.tsx</code></p>
              <p><strong>Key Prop:</strong> <code className="bg-gray-700 px-2 py-1 rounded">isHost: boolean</code> - Determines which buttons to show</p>
              <p><strong>HOST Usage:</strong> When <code className="bg-gray-700 px-2 py-1 rounded">mode === 'gig' && jamSessionRole === 'host'</code></p>
              <p><strong>CLIENT Usage:</strong> When <code className="bg-gray-700 px-2 py-1 rounded">isReadOnlyMode === true</code> (jam session client)</p>
              <p><strong>Design:</strong> Same layout as GigHeader, but purple theme and role-based buttons</p>
            </div>
          </div>
        </div>

        {/* Exit Preview */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
          >
            ← Return to Main App
          </a>
        </div>
      </div>
    </div>
  );
}
